import { useState, useEffect } from 'react';
import { adminService } from '../services/api';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Mail, MessageCircle, Calendar, Tag } from 'lucide-react';

const Feedbacks = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFeedbacks();
  }, []);

  const fetchFeedbacks = async () => {
    try {
      const { data } = await adminService.getFeedbacks();
      setFeedbacks(data.feedbacks);
    } catch (error) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await adminService.updateFeedbackStatus(id, newStatus);
      // Update local state to reflect change instantly without reloading
      setFeedbacks(feedbacks.map(f => f._id === id ? { ...f, status: newStatus } : f));
      toast.success(`Ticket marked as ${newStatus}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'open': return 'bg-red-500/10 text-red-400 border-red-500/20';
      case 'in-progress': return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
      case 'resolved': return 'bg-green-500/10 text-green-400 border-green-500/20';
      default: return 'bg-gray-800 text-gray-400 border-gray-700';
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white font-sans selection:bg-green-500/30 pb-12">
      {/* Navigation */}
      <nav className="border-b border-gray-800 bg-[#111111]/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center">
          <Link to="/interface" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm font-medium">
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Dashboard</span>
          </Link>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-8 gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <MessageCircle className="w-6 h-6 text-gray-400" />
              Support Pipeline
            </h1>
            <p className="text-gray-400 text-sm mt-1">Manage bug reports, features, and billing inquiries.</p>
          </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="p-12 text-center text-gray-500 font-mono text-sm bg-[#111111] border border-gray-800 rounded-xl">
            Loading pipeline...
          </div>
        ) : feedbacks.length === 0 ? (
          <div className="p-12 text-center text-gray-500 font-mono text-sm bg-[#111111] border border-gray-800 rounded-xl">
            No tickets found. Inbox zero! 🎉
          </div>
        ) : (
          <div className="bg-[#111111] border border-gray-800 rounded-xl shadow-sm overflow-hidden">
            
            {/* MOBILE VIEW: Stacked Cards (Hidden on md screens and up) */}
            <div className="md:hidden flex flex-col divide-y divide-gray-800/50">
              {feedbacks.map((fb) => (
                <div key={fb._id} className="p-5 flex flex-col gap-4 hover:bg-gray-800/20 transition-colors">
                  
                  {/* Top Row: User & Status */}
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-medium text-white text-sm">{fb.user?.name || 'Unknown User'}</div>
                      <div className="flex items-center gap-1.5 text-gray-500 text-xs mt-1">
                        <Mail className="w-3 h-3" /> <span className="truncate w-32 sm:w-auto">{fb.email}</span>
                      </div>
                    </div>
                    <div>
                      <select 
                        value={fb.status}
                        onChange={(e) => handleStatusChange(fb._id, e.target.value)}
                        className={`px-2.5 py-1 rounded text-xs font-bold uppercase tracking-wider border appearance-none cursor-pointer outline-none shadow-sm ${getStatusColor(fb.status)}`}
                      >
                        <option value="open" className="bg-[#0a0a0a] text-white">Open</option>
                        <option value="in-progress" className="bg-[#0a0a0a] text-white">In Progress</option>
                        <option value="resolved" className="bg-[#0a0a0a] text-white">Resolved</option>
                      </select>
                    </div>
                  </div>

                  {/* Middle Row: Tags & Date */}
                  <div className="flex items-center gap-4 text-xs">
                    <span className="flex items-center gap-1 text-gray-400 bg-gray-800/50 px-2 py-1 rounded border border-gray-700">
                      <Tag className="w-3 h-3" /> {fb.type}
                    </span>
                    <span className="flex items-center gap-1 text-gray-500 font-mono">
                      <Calendar className="w-3 h-3" /> {new Date(fb.createdAt).toLocaleDateString()}
                    </span>
                  </div>

                  {/* Bottom Row: Message Content */}
                  <div className="bg-[#0a0a0a] rounded-lg p-3.5 border border-gray-800/60">
                    <div className="text-sm font-medium text-gray-200 mb-1">{fb.subject}</div>
                    <div className="text-xs text-gray-400 leading-relaxed whitespace-pre-wrap">
                      {fb.message}
                    </div>
                  </div>

                </div>
              ))}
            </div>

            {/* DESKTOP VIEW: Table (Hidden on small screens) */}
            <div className="hidden md:block">
              <table className="w-full text-left text-sm whitespace-nowrap">
                <thead className="bg-[#0a0a0a] text-gray-400 border-b border-gray-800 uppercase text-xs font-semibold tracking-wider">
                  <tr>
                    <th className="px-6 py-4">Developer</th>
                    <th className="px-6 py-4">Ticket Details</th>
                    <th className="px-6 py-4">Date</th>
                    <th className="px-6 py-4 text-right">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800/50 text-gray-300">
                  {feedbacks.map((fb) => (
                    <tr key={fb._id} className="hover:bg-gray-800/20 transition-colors">
                      <td className="px-6 py-4 align-top">
                        <div className="font-medium text-white">{fb.user?.name || 'Unknown User'}</div>
                        <div className="flex items-center gap-1.5 text-gray-500 text-xs mt-1">
                          <Mail className="w-3 h-3" /> {fb.email}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-1.5 max-w-md">
                          <div className="flex items-center gap-2">
                            <span className="bg-gray-800 text-gray-300 px-2 py-0.5 rounded text-[10px] border border-gray-700 uppercase tracking-wider font-bold">
                              {fb.type}
                            </span>
                            <span className="font-medium text-gray-200 truncate">{fb.subject}</span>
                          </div>
                          <div className="text-gray-500 text-xs truncate" title={fb.message}>
                            {fb.message}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 align-top text-gray-400 text-xs font-mono">
                        {new Date(fb.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 align-top text-right">
                        <select 
                          value={fb.status}
                          onChange={(e) => handleStatusChange(fb._id, e.target.value)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider border appearance-none cursor-pointer outline-none shadow-sm text-center ${getStatusColor(fb.status)}`}
                        >
                          <option value="open" className="bg-[#0a0a0a] text-white">Open</option>
                          <option value="in-progress" className="bg-[#0a0a0a] text-white">In Progress</option>
                          <option value="resolved" className="bg-[#0a0a0a] text-white">Resolved</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Feedbacks;